<template>
  <button
    :class="buttonClasses"
    :disabled="disabled || loading"
    :type="type"
    @click="handleClick"
  >
    <span v-if="loading" class="btn-spinner">⏳</span>
    <span v-if="loading && loadingText">{{ loadingText }}</span>
    <span v-else class="btn-content">
      <slot name="icon">
        <img
          v-if="icon"
          :src="icon"
          :alt="iconAlt || ''"
          class="btn-icon-img"
        />
      </slot>
      <slot></slot>
    </span>
  </button>
</template>

<script>
export default {
  name: "Button",
  props: {
    variant: {
      type: String,
      default: "primary",
    },
    size: {
      type: String,
      default: "medium",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingText: {
      type: String,
      default: "",
    },
    block: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: "",
    },
    iconAlt: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "button",
    },
  },
  computed: {
    buttonClasses() {
      return [
        "btn",
        `btn--${this.variant}`,
        `btn--${this.size}`,
        {
          "btn--block": this.block,
          "btn--loading": this.loading,
          "btn--disabled": this.disabled || this.loading,
          "btn--active": this.active,
        },
      ];
    },
  },
  methods: {
    handleClick(event) {
      if (!this.disabled && !this.loading) {
        this.$emit("click", event);
      }
    },
  },
};
</script>

<style scoped>
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: "Pptelegraf-Bold", sans-serif;
  font-weight: 600;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  white-space: nowrap;
  user-select: none;
  position: relative;
}

.btn--small {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  border-radius: 6px;
}

.btn--medium {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  border-radius: 8px;
}

.btn--large {
  padding: 1rem 2rem;
  font-size: 1.125rem;
  border-radius: 12px;
}

.btn--primary {
  background: var(--surface);
  border: none;
}

/* Active state - primary color background en contrasting tekst */
.btn--primary.btn--active {
  background: var(--primary_color);
  color: var(--background);
  border: none;
}

.btn--secondary {
  background: var(--surface, #fff);
  border: none;
}

.btn--secondary.btn--active {
  background: var(--primary_color);
  color: var(--background);
}

.btn--outline {
  background: transparent;
  color: var(--primary_color);
  border: 2px solid var(--primary_color);
}

.btn--outline.btn--active {
  background: var(--primary_color);
  color: var(--background);
}

.btn--ghost {
  background: transparent;
  color: var(--primary_color);
  border: 2px solid transparent;
}

.btn--ghost.btn--active {
  background: var(--primary_color);
  color: var(--background);
  border-color: var(--primary_color);
}

.btn--danger {
  background: #ef4444;
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.15);
}

.btn--danger.btn--active {
  background: #ef4444;
  color: #ffffff;
  border: 2px solid #ef4444;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.35);
}

/* General active state styling */
.btn--active {
  font-weight: 700;
  transition: all 0.2s ease;
}

/* Button content and icons */
.btn-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-icon-img {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

/* Icon filters for different button states */
.btn--primary:not(.btn--active) .btn-icon-img,
.btn--secondary:not(.btn--active) .btn-icon-img,
.btn--outline:not(.btn--active) .btn-icon-img,
.btn--ghost:not(.btn--active) .btn-icon-img {
  filter: brightness(0) saturate(100%) invert(50%) sepia(100%) saturate(5000%)
    hue-rotate(90deg);
}

.btn--primary.btn--active .btn-icon-img,
.btn--secondary.btn--active .btn-icon-img,
.btn--outline.btn--active .btn-icon-img,
.btn--ghost.btn--active .btn-icon-img {
  filter: brightness(0) invert(1);
}

/* Dark mode specific icon adjustments */
[data-theme="dark"] .btn--primary:not(.btn--active) .btn-icon-img,
[data-theme="dark"] .btn--secondary:not(.btn--active) .btn-icon-img,
[data-theme="dark"] .btn--outline:not(.btn--active) .btn-icon-img,
[data-theme="dark"] .btn--ghost:not(.btn--active) .btn-icon-img {
  filter: brightness(0) saturate(100%) invert(50%) sepia(100%) saturate(5000%)
    hue-rotate(90deg);
}

[data-theme="dark"] .btn--primary.btn--active .btn-icon-img,
[data-theme="dark"] .btn--secondary.btn--active .btn-icon-img,
[data-theme="dark"] .btn--outline.btn--active .btn-icon-img,
[data-theme="dark"] .btn--ghost.btn--active .btn-icon-img {
  filter: brightness(0) invert(0);
}

/* Light mode specific icon adjustments */
[data-theme="light"] .btn--primary:not(.btn--active) .btn-icon-img,
[data-theme="light"] .btn--secondary:not(.btn--active) .btn-icon-img,
[data-theme="light"] .btn--outline:not(.btn--active) .btn-icon-img,
[data-theme="light"] .btn--ghost:not(.btn--active) .btn-icon-img {
  filter: brightness(0) invert(0);
}

[data-theme="light"] .btn--primary.btn--active .btn-icon-img,
[data-theme="light"] .btn--secondary.btn--active .btn-icon-img,
[data-theme="light"] .btn--outline.btn--active .btn-icon-img,
[data-theme="light"] .btn--ghost.btn--active .btn-icon-img {
  filter: brightness(0) invert(1);
}

.btn--disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
  pointer-events: none;
}

.btn--loading {
  cursor: wait;
}

.btn--block {
  width: 100%;
}

.btn-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
